import { useState } from 'react'
import { Bell, Bot, Eye, MessageSquare, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { channelsLabel, formatCount } from './types'
import type { BroadcastFormData, OrganizationOption, Reach } from './types'

interface Props {
  data: BroadcastFormData
  organizations: OrganizationOption[]
  reach: Reach
  onUpdate: (patch: Partial<BroadcastFormData>) => void
  onReview: () => void
}

const inputClasses =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40'

function StepLabel({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="mb-2.5 mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground first:mt-0">
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-secondary text-[11px] font-bold text-foreground">
        {number}
      </span>
      {children}
    </div>
  )
}

function segmentClasses(active: boolean) {
  return `rounded px-3 py-1 text-xs font-medium transition-colors ${
    active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
  }`
}

export default function Composer({ data, organizations, reach, onUpdate, onReview }: Props) {
  const [search, setSearch] = useState('')
  const allMode = data.org_scope === 'all'
  const query = search.trim().toLowerCase()
  const visibleOrgs = organizations.filter((org) => !query || org.name.toLowerCase().includes(query))
  const channelsOn = data.channels.panel || data.channels.email
  // Espelha a validação do servidor: sem agentes, só dá para enviar se um canal
  // adicional alcançar usuários.
  const canReview =
    data.content.trim() !== '' &&
    reach.orgs > 0 &&
    (reach.agents > 0 || (channelsOn && reach.users > 0))

  const switchToCustom = () => {
    onUpdate({
      org_scope: 'custom',
      organization_ids:
        data.organization_ids.length > 0 ? data.organization_ids : organizations.map((org) => org.id),
    })
  }

  const toggleOrg = (id: string) => {
    onUpdate({
      organization_ids: data.organization_ids.includes(id)
        ? data.organization_ids.filter((orgId) => orgId !== id)
        : [...data.organization_ids, id],
    })
  }

  const usersStrong = (
    <strong className="font-semibold text-foreground">
      {formatCount(reach.users)} {reach.users === 1 ? 'usuário' : 'usuários'}
    </strong>
  )

  let impactLine: React.ReactNode
  if (reach.orgs === 0) {
    impactLine = 'Selecione ao menos uma organização.'
  } else if (reach.agents > 0) {
    impactLine = (
      <>
        Este comunicado alcança{' '}
        <strong className="font-semibold text-foreground">
          {formatCount(reach.agents)} {reach.agents === 1 ? 'agente' : 'agentes'}
        </strong>{' '}
        ({formatCount(reach.sessions)} {reach.sessions === 1 ? 'sessão' : 'sessões'}) em{' '}
        <strong className="font-semibold text-foreground">
          {formatCount(reach.orgs)} {reach.orgs === 1 ? 'organização' : 'organizações'}
        </strong>
        {channelsOn && <>, e notifica {usersStrong} via {channelsLabel(data.channels)}</>}.
      </>
    )
  } else if (channelsOn && reach.users > 0) {
    impactLine = (
      <>
        As organizações selecionadas não têm agentes — apenas {usersStrong}{' '}
        {reach.users === 1 ? 'será notificado' : 'serão notificados'} via {channelsLabel(data.channels)}.
      </>
    )
  } else {
    impactLine = 'As organizações selecionadas não têm agentes disponíveis.'
  }

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="p-5">
        <StepLabel number={1}>Mensagem</StepLabel>
        <div className="mb-3.5">
          <label htmlFor="broadcast-title" className="mb-1.5 block text-sm font-medium text-foreground">
            Título
          </label>
          <input
            id="broadcast-title"
            type="text"
            maxLength={120}
            value={data.title}
            onChange={(event) => onUpdate({ title: event.target.value })}
            placeholder="Ex.: Nova função disponível: Processos v2"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="broadcast-content" className="mb-1.5 block text-sm font-medium text-foreground">
            Conteúdo
          </label>
          <textarea
            id="broadcast-content"
            maxLength={2000}
            value={data.content}
            onChange={(event) => onUpdate({ content: event.target.value })}
            placeholder="Escreva o comunicado. Agentes recebem o texto no contexto da sessão."
            className={`${inputClasses} min-h-[120px] resize-y`}
          />
          <p
            className={`mt-1 text-right text-[11px] ${
              data.content.length >= 2000 ? 'text-red-500' : 'text-muted-foreground'
            }`}
          >
            {data.content.length} / 2000
          </p>
        </div>

        <StepLabel number={2}>Canais de entrega</StepLabel>
        <div className="flex items-start gap-3 rounded-lg border border-red-500/35 bg-background px-3.5 py-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-red-500/10 text-red-500">
            <Bot className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              Agentes
              <span className="ml-2 rounded-full border border-red-500/35 bg-red-500/10 px-2 py-px align-middle text-[10px] font-bold uppercase tracking-wide text-red-500">
                Canal padrão
              </span>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Todo comunicado é entregue imediatamente a cada agente das organizações selecionadas, em todas
              as sessões de usuário do agente
            </p>
          </div>
        </div>
        <div className="mt-3 rounded-lg border border-border/60 bg-background/60 px-3.5 py-3">
          <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-foreground">
            <Bell className="h-3.5 w-3.5 text-muted-foreground" />
            Canais adicionais para usuários (opcional)
          </p>
          <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-[13px] text-foreground">
            <input
              type="checkbox"
              checked={data.channels.panel}
              onChange={(event) => onUpdate({ channels: { ...data.channels, panel: event.target.checked } })}
              className="accent-red-500"
            />
            Notificação no painel
          </label>
          <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-[13px] text-foreground">
            <input
              type="checkbox"
              checked={data.channels.email}
              onChange={(event) => onUpdate({ channels: { ...data.channels, email: event.target.checked } })}
              className="accent-red-500"
            />
            E-mail
          </label>
        </div>

        <StepLabel number={3}>Organizações</StepLabel>
        <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
          <div className="inline-flex rounded-md border border-border bg-background p-0.5">
            <button type="button" onClick={() => onUpdate({ org_scope: 'all' })} className={segmentClasses(allMode)}>
              Todas
            </button>
            <button type="button" onClick={switchToCustom} className={segmentClasses(!allMode)}>
              Selecionar
            </button>
          </div>
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar organização..."
              aria-label="Buscar organização"
              className={`${inputClasses} py-1.5 pl-8 text-[13px]`}
            />
          </div>
        </div>
        <div
          className={`max-h-64 overflow-y-auto rounded-lg border border-border/60 bg-background ${
            allMode ? 'opacity-60' : ''
          }`}
        >
          {visibleOrgs.map((org) => (
            <label
              key={org.id}
              className="flex cursor-pointer items-center gap-2.5 border-b border-border/40 px-3 py-2 text-sm last:border-b-0 hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={allMode || data.organization_ids.includes(org.id)}
                disabled={allMode}
                onChange={() => toggleOrg(org.id)}
                className="accent-red-500"
              />
              <span className="font-medium text-foreground">{org.name}</span>
              {org.eligibleAgentsCount === 0 && (
                <span className="text-[10px] font-semibold text-amber-500">sem agentes</span>
              )}
              <span className="ml-auto flex gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1" title="Agentes">
                  <Bot className="h-3 w-3" />
                  {org.eligibleAgentsCount}
                </span>
                <span className="inline-flex items-center gap-1" title="Sessões ativas">
                  <MessageSquare className="h-3 w-3" />
                  {org.activeSessionsCount}
                </span>
                <span className="inline-flex items-center gap-1" title="Usuários">
                  <Users className="h-3 w-3" />
                  {org.usersCount}
                </span>
              </span>
            </label>
          ))}
          {visibleOrgs.length === 0 && (
            <p className="px-3.5 py-3.5 text-xs text-muted-foreground">
              Nenhuma organização encontrada para “{search}”.
            </p>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {allMode
            ? `Todas as ${organizations.length} organizações da plataforma serão incluídas.`
            : `${data.organization_ids.length} de ${organizations.length} organizações selecionadas.`}
        </p>
      </div>

      <div className="flex items-center gap-3.5 border-t border-border px-5 py-3.5">
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">{impactLine}</p>
        <Button size="sm" disabled={!canReview} onClick={onReview}>
          <Eye className="h-4 w-4" />
          Revisar e enviar
        </Button>
      </div>
    </section>
  )
}
